import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useOrderAttention, ATTENTION_LABEL } from "@/context/OrderAttentionContext";
import { Search, Printer, CheckCircle2, XCircle } from "lucide-react";
import api, { formatRp, imageUrl, formatApiError, notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import TrackingPanel from "@/components/TrackingPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CancelOrderDialog, RefundStatusBadge, CancellationRequestStatus, CancellationSummary } from "@/components/OrderCancellation";
import { allowedAdminTargets, cancelEligibility, refundOf, requestOf, cancellationPresentation, MANUAL_PAYMENT_METHODS } from "@/lib/orderCancellation";
import { ApproveCancellationDialog, RejectCancellationDialog } from "@/components/admin/orders/CancellationDecisionDialogs";
import { PaymentNotFoundDialog } from "@/components/admin/orders/PaymentNotFoundDialog";
import { BiteshipShipmentPanel } from "@/components/admin/orders/BiteshipShipmentPanel";
import { hasDeadlineLifecycle, serverOffsetMs } from "@/lib/paymentDeadline";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/constants/featureRegistry";

const STATUS_LABEL = {
  pending_payment: "Menunggu Bayar",
  cod_pending: "COD · Menunggu Konfirmasi",
  cod_return_pending: "COD · Retur Menunggu Konfirmasi",
  cod_returned: "COD · Retur Ditutup",
  paid: "Diverifikasi",
  processing: "Diproses",
  shipped: "Dikirim",
  completed: "Selesai",
  cancelled: "Dibatalkan",
};
const STATUSES = Object.keys(STATUS_LABEL);

const ATTENTION_KEYS = Object.keys(ATTENTION_LABEL);

export function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const attention = ATTENTION_KEYS.includes(searchParams.get("attention")) ? searchParams.get("attention") : null;
  const setAttention = (key) => setSearchParams(key ? { attention: key } : {});

  const load = useCallback(() => {
    // Membuka/memfilter LIST tidak pernah menandai seen; definisi filter = server (/order-attention).
    const params = { limit: 200 };
    if (attention) params.attention = attention;
    else if (tab !== "all") params.status = tab;
    if (search) params.search = search;
    api.get("/admin/orders", { params }).then((r) => setOrders(r.data.items));
  }, [tab, search, attention]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl font-black mb-4">Pesanan</h1>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="attention-filters" role="group" aria-label="Filter perhatian">
        {ATTENTION_KEYS.map((k) => (
          <button key={k} type="button" onClick={() => setAttention(attention === k ? null : k)} aria-pressed={attention === k}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${attention === k ? "brand-bg-primary text-white border-transparent" : "bg-white border-[#E5E5E5] text-[#5C5C5C] hover:bg-[#FAF9F6]"}`}
            data-testid={`attention-filter-${k}`}>{ATTENTION_LABEL[k]}</button>
        ))}
        {attention && <button type="button" onClick={() => setAttention(null)} className="px-3 py-1.5 rounded-full text-xs font-semibold text-[#E85D04]" data-testid="attention-filter-clear">Hapus filter</button>}
      </div>
      <Tabs value={attention ? "" : tab} onValueChange={(v) => { setAttention(null); setTab(v); }}>
        <TabsList className="bg-white border border-[#E5E5E5] rounded-xl p-1 w-full overflow-x-auto no-scrollbar justify-start">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#FFEDD5] data-[state=active]:text-[#E85D04] rounded-lg whitespace-nowrap">Semua</TabsTrigger>
          {STATUSES.map((s) => (
            <TabsTrigger key={s} value={s} className="data-[state=active]:bg-[#FFEDD5] data-[state=active]:text-[#E85D04] rounded-lg whitespace-nowrap" data-testid={`admin-orders-tab-${s}`}>{STATUS_LABEL[s]}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="bg-white border border-[#E5E5E5] rounded-2xl mt-4" data-tour="orders-page">
        <div className="p-4 border-b border-[#E5E5E5]">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={16} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari no pesanan / pelanggan..." className="pl-9 rounded-xl" data-testid="admin-orders-search" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold border-b border-[#E5E5E5]">
                <th className="px-4 py-3">No Pesanan</th>
                <th className="px-4 py-3">Pelanggan</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Pembayaran</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Tanggal</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FAF9F6]">
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><Link to={`/admin/pesanan/${o.id}`} className="font-mono text-xs text-[#E85D04] font-bold" data-testid={`admin-order-${o.order_number}`}>{o.order_number}</Link>{o.is_new && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FFEDD5] text-[#E85D04]" aria-label="Pesanan baru belum dilihat" data-testid={`order-new-badge-${o.order_number}`}>BARU</span>}</div></td>
                  <td className="px-4 py-3"><div className="font-semibold">{o.user_name}</div><div className="text-xs text-[#5C5C5C]">{o.user_email}</div></td>
                  <td className="px-4 py-3 font-bold">{formatRp(o.total)}</td>
                  <td className="px-4 py-3 uppercase text-xs">{o.payment_method}</td>
                  <td className="px-4 py-3"><div className="flex items-center gap-1.5 flex-wrap"><span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-[#FFEDD5] text-[#E85D04]">{STATUS_LABEL[o.status]}</span>{cancellationPresentation(o)?.badge && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid={`order-cancel-badge-${o.order_number}`}>{cancellationPresentation(o).badge}</span>}</div></td>
                  <td className="px-4 py-3 text-xs text-[#5C5C5C]">{new Date(o.created_at).toLocaleDateString("id-ID")}</td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-[#5C5C5C]">Tidak ada pesanan</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

export function AdminOrderDetail() {
  const { orderId } = useParams();
  const [o, setO] = useState(null);
  const [status, setStatus] = useState("");
  const [tracking, setTracking] = useState("");
  const [settings, setSettings] = useState(null);
  const [jubelio, setJubelio] = useState(null); // {_active, sandbox, enabled, ...}
  const [jubBusy, setJubBusy] = useState(false);
  const [jubStatus, setJubStatus] = useState(null);
  // Phase 4: Tracking
  const [trackingDetail, setTrackingDetail] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Buat Resi state (dry-run/production choice removed from tenant UI —
  // backend derives real-resi permission from `enabled` on save).
  const [awbConfirmOpen, setAwbConfirmOpen] = useState(false);
  const [awbResult, setAwbResult] = useState(null);
  // Cancel AWB modal state
  const [cancelOpen, setCancelOpen] = useState(false);
  const [orderCancelOpen, setOrderCancelOpen] = useState(false);
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundForm, setRefundForm] = useState({ amount: "", method: "transfer_bank", reference: "", note: "" });
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  // Phase 3: keputusan pengajuan pembatalan dari halaman detail (dialog bersama dengan workspace Pembatalan)
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectReqOpen, setRejectReqOpen] = useState(false);
  const [notFoundOpen, setNotFoundOpen] = useState(false); // Phase 4C
  const { user } = useAuth();
  const canUpdate = hasPermission(user, "orders.update");

  const { markSeen, refresh: refreshAttention } = useOrderAttention();
  const seenOnce = useRef(null);

  const load = useCallback(async () => {
    const r = await api.get(`/admin/orders/${orderId}`);
    setO(r.data);
    setStatus(r.data.status);
    setTracking(r.data.tracking_number || "");
    if (seenOnce.current !== orderId) { seenOnce.current = orderId; markSeen(orderId); } // detail dibuka → seen (sekali)
    else refreshAttention(); // aksi admin (verifikasi/status/refund/cancel) → counter segar tanpa menunggu 30s
  }, [orderId, markSeen, refreshAttention]);

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/admin/settings/jubelio").then((r) => setJubelio(r.data)).catch(() => {});
    api.get(`/admin/orders/${orderId}/jubelio/tracking`).then((r) => setTrackingDetail(r.data)).catch(() => {});
  }, [load, orderId]);

  if (!o) return <AdminLayout><div className="p-12 text-center text-[#5C5C5C]">Memuat...</div></AdminLayout>;
  const requestPending = requestOf(o)?.status === "REQUESTED";
  // Phase 4C: aksi "Pembayaran Tidak Ditemukan" hanya untuk klaim manual yang menunggu verifikasi.
  const canPaymentNotFound = canUpdate && MANUAL_PAYMENT_METHODS.includes(o.payment_method)
    && o.status === "pending_payment" && o.payment_status === "pending_verification";
  const deadlineInfo = hasDeadlineLifecycle(o) ? (() => {
    const nowMs = Date.now() + serverOffsetMs(o.server_now);
    const d = Date.parse(o.payment_deadline);
    const relevant = o.status === "pending_payment" && !["pending_verification", "verified", "paid"].includes(o.payment_status);
    return { at: new Date(d).toLocaleString("id-ID"), past: d <= nowMs, relevant, extended: (o.payment_deadline_extension_count || 0) > 0 };
  })() : null;

  const cancelOrderAdmin = async (reason) => {
    setRefundBusy(true);
    try {
      await api.post(`/admin/orders/${o.id}/cancel`, { reason });
      toast.success("Pesanan dibatalkan");
      setOrderCancelOpen(false);
      load();
    } catch (e) { notifyError(e); } finally { setRefundBusy(false); }
  };
  const refundProcessing = async () => {
    setRefundBusy(true);
    try { await api.post(`/admin/orders/${o.id}/refund/processing`); toast.success("Refund ditandai sedang diproses"); load(); }
    catch (e) { notifyError(e); } finally { setRefundBusy(false); }
  };
  const refundComplete = async () => {
    setRefundBusy(true);
    try {
      await api.post(`/admin/orders/${o.id}/refund/complete`, {
        amount: Number(refundForm.amount || refundOf(o).amount || 0), method: refundForm.method,
        reference: refundForm.reference || null, note: refundForm.note || null,
      });
      toast.success("Refund ditandai selesai"); load();
    } catch (e) { notifyError(e); } finally { setRefundBusy(false); }
  };

  const updateStatus = async () => {
    try {
      await api.put(`/admin/orders/${o.id}/status`, { status, tracking_number: tracking || null });
      toast.success("Status pesanan diperbarui");
      load();
    } catch (e) { notifyError(e); }
  };

  const verifyPayment = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${o.id}/verify-payment`);
      toast.success("Pembayaran diverifikasi");
      load();
    } catch (e) { notifyError(e); } finally { setBusy(false); }
  };

  const rejectPayment = async () => {
    if (!rejectReason.trim()) {
      toast.error("Alasan penolakan wajib diisi");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/orders/${o.id}/reject-payment`, { reason: rejectReason.trim() });
      toast.success("Pembayaran ditolak");
      setRejectOpen(false);
      setRejectReason("");
      load();
    } catch (e) { notifyError(e); } finally { setBusy(false); }
  };

  const printInvoice = () => window.print();

  const isQris = o.payment_method === "qris";

  const jubelioActive = !!jubelio?._active;
  const hasAwb = !!(o.awb || o.jubelio_shipment_id);

  // Phase 4: Tracking — fetch cached + refresh dari Jubelio
  const fetchTracking = async () => {
    try {
      const { data } = await api.get(`/admin/orders/${o.id}/jubelio/tracking`);
      setTrackingDetail(data);
    } catch (e) { /* silent */ }
  };

  const refreshTracking = async () => {
    try {
      await api.post(`/admin/orders/${o.id}/jubelio/tracking/sync`);
      toast.success("Tracking berhasil diperbarui");
      await fetchTracking();
      await load();
    } catch (e) {
      notifyError(e);
    }
  };

  const copyAwb = async () => {
    const value = o.awb || o.tracking_number;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`Resi "${value}" disalin ke clipboard`);
    } catch {
      // Fallback for non-HTTPS / older browsers
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success(`AWB "${value}" disalin`); }
      finally { document.body.removeChild(ta); }
    }
  };

  const runGenerateAwb = async () => {
    setJubBusy(true);
    try {
      const { data } = await api.post(
        `/admin/orders/${o.id}/jubelio/create-shipment`,
        { dry_run: false },
      );
      setAwbResult(data);
      toast.success(`Resi berhasil dibuat: ${data.awb || "-"}`);
      setAwbConfirmOpen(false);
      load();
    } catch (e) { notifyError(e); }
    finally { setJubBusy(false); }
  };

  const fetchJubStatus = async () => {
    setJubBusy(true);
    try {
      const { data } = await api.get(`/admin/orders/${o.id}/jubelio/status`);
      setJubStatus(data);
      toast.success(`Status: ${data?.tracking_status || data?.sync?.status || "tersinkron"}`);
      // Phase 4: sync UI tracking panel + order detail
      await fetchTracking();
      await load();
    } catch (e) { notifyError(e); }
    finally { setJubBusy(false); }
  };

  const cancelJubShipment = async () => {
    const reason = (cancelReason || "").trim();
    if (reason.length < 3) {
      toast.error("Alasan pembatalan wajib diisi (minimal 3 karakter)");
      return;
    }
    setCancelBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/jubelio/cancel`, { reason });
      toast.success(`Resi ${data?.awb || o.awb || ""} berhasil dibatalkan`);
      setCancelOpen(false);
      setCancelReason("");
      // Refresh tracking panel + order detail langsung
      await fetchTracking();
      await load();
    } catch (e) {
      notifyError(e);
    } finally {
      setCancelBusy(false);
    }
  };

  return (
    <AdminLayout>
      <Link to="/admin/pesanan" className="text-sm text-[#5C5C5C] hover:text-[#E85D04]">← Kembali</Link>
      <div className="flex items-center justify-between mt-2 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black">{o.order_number}</h1>
          <p className="text-sm text-[#5C5C5C]">{new Date(o.created_at).toLocaleString("id-ID")}</p>
        </div>
        <Button variant="outline" onClick={printInvoice} className="rounded-xl"><Printer size={14} className="mr-1" /> Cetak Invoice</Button>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-4 mt-6">
        <div className="space-y-4">
          <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
            <h3 className="font-display font-bold mb-3">Item Pesanan</h3>
            <div className="space-y-3">
              {o.items.map((it, i) => (
                <div key={`${it.product_id}-${i}`} className="flex gap-3 pb-3 border-b border-[#E5E5E5] last:border-0 last:pb-0">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#FAF9F6]">
                    {it.image && <img src={imageUrl(it.image)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{it.name}</div>
                    <div className="text-xs text-[#5C5C5C]">{it.quantity} {it.unit} × {formatRp(it.unit_price)}</div>
                  </div>
                  <div className="font-bold">{formatRp(it.subtotal)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-[#E5E5E5] space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[#5C5C5C]">Subtotal</span><span>{formatRp(o.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#5C5C5C]">Ongkir ({o.shipping_method_name})</span><span>{formatRp(o.shipping_cost)}</span></div>
              {o.discount > 0 && <div className="flex justify-between text-[#2D6A4F]"><span>Diskon</span><span>-{formatRp(o.discount)}</span></div>}
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-[#E5E5E5]"><span>Total</span><span className="text-[#E85D04]">{formatRp(o.total)}</span></div>
            </div>
          </section>

          <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
            <h3 className="font-display font-bold mb-2">Pelanggan & Pengiriman</h3>
            <div className="text-sm space-y-1">
              <div><b>{o.user_name}</b> · {o.user_email}</div>
              <div className="text-[#5C5C5C]">{o.address_snapshot?.recipient_name} · {o.address_snapshot?.phone}</div>
              <div className="text-[#5C5C5C]">{o.address_snapshot?.address}, {o.address_snapshot?.city} {o.address_snapshot?.postal_code}</div>
              {o.notes && <div className="mt-2"><b>Catatan:</b> {o.notes}</div>}
            </div>
          </section>

          {o.payment_method === "midtrans" && o.midtrans && (
            <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="admin-midtrans-info">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-display font-bold">Pembayaran Online — Midtrans</h3>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${
                  o.payment_status === "paid" ? "bg-[#2D6A4F]/15 text-[#2D6A4F]"
                  : o.payment_status === "pending" ? "bg-[#FFB703]/15 text-[#B5790F]"
                  : o.payment_status === "failed" ? "bg-red-100 text-red-700"
                  : o.payment_status === "expired" ? "bg-gray-200 text-gray-700"
                  : o.payment_status === "cancelled" ? "bg-red-100 text-red-700"
                  : "bg-blue-100 text-blue-700"
                }`} data-testid="midtrans-payment-status">
                  {o.payment_status === "paid" ? "Paid"
                    : o.payment_status === "pending" ? "Pending"
                    : o.payment_status === "failed" ? "Failed"
                    : o.payment_status === "expired" ? "Expired"
                    : o.payment_status === "cancelled" ? "Cancelled"
                    : (o.payment_status || "Created")}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">Metode</div>
                  <div className="font-bold">Midtrans Snap</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">Tipe Pembayaran</div>
                  <div className="font-mono text-xs break-all">{o.midtrans.payment_type || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">Transaction ID</div>
                  <div className="font-mono text-xs break-all">{o.midtrans.transaction_id || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">Order ID Midtrans</div>
                  <div className="font-mono text-xs break-all">{o.midtrans.order_id || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">Gross Amount</div>
                  <div className="font-bold text-[#E85D04]">{formatRp(o.midtrans.gross_amount || o.total)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C]">Paid At</div>
                  <div>{o.paid_at ? new Date(o.paid_at).toLocaleString("id-ID") : "—"}</div>
                </div>
              </div>
              {o.payment_status === "paid" && (
                <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-800">
                  ✓ Pembayaran terverifikasi otomatis via webhook Midtrans. Tidak perlu upload/verifikasi bukti manual.
                </div>
              )}
              {o.payment_status !== "paid" && o.payment_status !== "expired" && o.payment_status !== "cancelled" && (
                <div className="mt-3">
                  <Button
                    onClick={async () => {
                      try {
                        const r = await api.post(`/admin/orders/${o.id}/midtrans/check-status`, {});
                        toast.success(`Status terbaru: ${r.data.transaction_status || "?"} → ${r.data.payment_status || "?"}`);
                        load?.();
                      } catch (e) { notifyError(e); }
                    }}
                    variant="outline"
                    className="rounded-xl border-[#E85D04] text-[#E85D04] hover:bg-[#FFEDD5]"
                    data-testid="admin-midtrans-check-status-btn"
                  >
                    Cek Status Midtrans
                  </Button>
                  <div className="text-[10px] text-[#5C5C5C] mt-1">
                    Fallback kalau webhook gagal/lambat — query langsung ke Midtrans Status API.
                  </div>
                </div>
              )}
              {o.midtrans.webhook_events?.length > 0 && (
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-[#5C5C5C] hover:text-[#E85D04] font-bold">Riwayat Webhook ({o.midtrans.webhook_events.length})</summary>
                  <div className="mt-2 space-y-1 max-h-48 overflow-auto">
                    {o.midtrans.webhook_events.slice().reverse().map((ev, i) => (
                      <div key={`mt-evt-${i}`} className="bg-[#FAF9F6] rounded p-2 font-mono">
                        <div>{ev.at} · <b>{ev.transaction_status}</b>{ev.fraud_status ? ` (${ev.fraud_status})` : ""}</div>
                        {ev.payment_type && <div className="text-[#5C5C5C]">type: {ev.payment_type}</div>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </section>
          )}

          {(o.payment_proof_path || (o.payment_status === "pending_verification" && o.status !== "cancelled")) && (
            <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="admin-payment-verification">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-display font-bold">Verifikasi Pembayaran</h3>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${
                  o.payment_status === "verified" ? "bg-[#2D6A4F]/15 text-[#2D6A4F]"
                  : o.payment_status === "rejected" ? "bg-red-100 text-red-700"
                  : "bg-[#FFB703]/15 text-[#B5790F]"
                }`}>
                  {o.payment_status === "verified" ? "Diverifikasi" : o.payment_status === "rejected" ? "Ditolak" : "Menunggu Verifikasi"}
                </span>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C] mb-2">Metode Pembayaran</div>
                  <div className="font-bold uppercase">{o.payment_method}</div>
                  <div className="text-xs text-[#5C5C5C] mt-1">Total: <b className="text-[#E85D04]">{formatRp(o.total)}</b></div>
                  {isQris && settings?.qris_image && (
                    <div className="mt-3">
                      <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C] mb-1">QRIS yang Digunakan</div>
                      <img src={imageUrl(settings.qris_image)} alt="QRIS" className="w-32 h-32 object-contain bg-white border border-[#E5E5E5] rounded-lg p-2" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-[#5C5C5C] mb-2">Bukti Pembayaran Pelanggan</div>
                  {o.payment_proof_path ? (
                    <a href={imageUrl(o.payment_proof_path)} target="_blank" rel="noreferrer">
                      <img src={imageUrl(o.payment_proof_path)} alt="Bukti" className="max-h-64 rounded-xl border border-[#E5E5E5] hover:opacity-90 transition-opacity" data-testid="admin-payment-proof-img" />
                    </a>
                  ) : (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="admin-claim-without-proof">
                      Pelanggan menekan <b>Sudah Membayar</b> tanpa mengunggah bukti{o.payment_claimed_at ? ` (${new Date(o.payment_claimed_at).toLocaleString("id-ID")})` : ""}.
                      <b> Periksa mutasi bank/QRIS</b> sebelum memverifikasi — verifikasi akan dicatat sebagai konfirmasi tanpa bukti.
                      {o.payment_claim_note && <div className="mt-1">Catatan pelanggan: {o.payment_claim_note}</div>}
                    </div>
                  )}
                  {o.payment_rejection_reason && (
                    <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
                      <b>Alasan ditolak sebelumnya:</b> {o.payment_rejection_reason}
                    </div>
                  )}
                </div>
              </div>

              {o.payment_status !== "verified" && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-[#E5E5E5] flex-wrap">
                  <Button
                    onClick={verifyPayment}
                    disabled={busy}
                    className="bg-[#2D6A4F] hover:bg-[#1f4d3a] rounded-xl font-bold"
                    data-testid="verify-payment-btn"
                  >
                    <CheckCircle2 size={16} className="mr-1" /> Verifikasi Pembayaran
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setRejectOpen(true)}
                    disabled={busy}
                    className="rounded-xl text-[#DC2F02] border-[#DC2F02] hover:bg-red-50 hover:text-[#DC2F02]"
                    data-testid="reject-payment-btn"
                  >
                    <XCircle size={16} className="mr-1" /> Tolak Pembayaran
                  </Button>
                  {canPaymentNotFound && (
                    <Button variant="outline" onClick={() => setNotFoundOpen(true)} disabled={busy}
                      className="rounded-xl text-red-700 border-red-300 hover:bg-red-50 hover:text-red-700" data-testid="payment-not-found-btn">
                      Pembayaran Tidak Ditemukan / Batalkan Pesanan
                    </Button>
                  )}
                </div>
              )}
              <PaymentNotFoundDialog open={notFoundOpen} order={o} onClose={() => setNotFoundOpen(false)} onDone={() => { setNotFoundOpen(false); load(); }} />
            </section>
          )}
        </div>

        <aside className="bg-white border border-[#E5E5E5] rounded-2xl p-5 h-fit">
          <h3 className="font-display font-bold mb-3">Update Status</h3>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs uppercase font-bold tracking-widest">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1" data-testid="admin-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[o.status, ...allowedAdminTargets(o.status, o)].map((s) => <SelectItem key={s} value={s} disabled={s === o.status}>{STATUS_LABEL[s] || s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase font-bold tracking-widest">No Resi</label>
              <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Nomor resi pengiriman" className="rounded-xl" data-testid="admin-tracking-input" />
            </div>
            <Button onClick={updateStatus} disabled={status === o.status && tracking === (o.tracking_number || "")} className="w-full brand-btn-primary rounded-xl" data-testid="admin-update-status-btn">Simpan</Button>
            {deadlineInfo && (
              <div className={`mt-3 rounded-xl border p-3 text-xs ${deadlineInfo.relevant && deadlineInfo.past ? "bg-amber-50 border-amber-300 text-amber-900" : "bg-[#FAF9F6] border-[#E5E5E5] text-[#5C5C5C]"}`} data-testid="admin-payment-deadline" data-deadline-past={String(deadlineInfo.past)}>
                <div className="text-[10px] uppercase tracking-widest font-bold">Batas Waktu Pembayaran</div>
                <div className="font-semibold text-sm text-[#1A1A1A]">{deadlineInfo.at}</div>
                {deadlineInfo.extended && <div data-testid="admin-deadline-extended">Diperpanjang 1× oleh toko</div>}
                {deadlineInfo.relevant && deadlineInfo.past && !requestPending && <div data-testid="admin-deadline-past">Sudah berakhir — sistem akan membatalkan otomatis bila tidak ada klaim pembayaran.</div>}
                {requestPending && <div data-testid="admin-deadline-paused">{deadlineInfo.past ? "Sudah berakhir. " : ""}Timeout dijeda selama pengajuan pembatalan ditinjau; bila ditolak tanpa perpanjangan, sistem dapat membatalkan otomatis.</div>}
              </div>
            )}
            {requestOf(o) && (
              <div className="mt-3 space-y-2" data-testid="admin-cancellation-request-panel">
                <CancellationRequestStatus order={o} audience="admin" />
                {requestPending && (canUpdate ? (
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 rounded-lg brand-btn-primary" onClick={() => setApproveOpen(true)} data-testid="admin-approve-request-btn">Setujui</Button>
                    <Button size="sm" variant="outline" className="flex-1 rounded-lg text-red-600 border-red-200" onClick={() => setRejectReqOpen(true)} data-testid="admin-reject-request-btn">Tolak</Button>
                  </div>
                ) : <div className="text-xs text-[#5C5C5C]" data-testid="admin-request-readonly">Anda tidak memiliki izin memutuskan pengajuan ini.</div>)}
                <Link to="/admin/pesanan/pembatalan?tab=requested" className="block text-xs font-bold text-[#E85D04]" data-testid="admin-request-workspace-link">Buka workspace Pembatalan →</Link>
              </div>
            )}
            <ApproveCancellationDialog open={approveOpen} order={o} onClose={() => setApproveOpen(false)} onDone={() => { setApproveOpen(false); load(); }} />
            <RejectCancellationDialog open={rejectReqOpen} order={o} onClose={() => setRejectReqOpen(false)} onDone={() => { setRejectReqOpen(false); load(); }} />
            {!requestPending && cancelEligibility(o) === "allowed" && (
              <Button variant="outline" onClick={() => setOrderCancelOpen(true)} className="w-full rounded-xl text-red-600 border-red-200 mt-2" data-testid="admin-cancel-order-btn">
                Batalkan Pesanan
              </Button>
            )}
            {!requestPending && cancelEligibility(o) === "verification_pending" && (
              <div className="mt-2 text-xs text-[#B5790F]" data-testid="admin-cancel-blocked-verification">Selesaikan verifikasi bukti bayar sebelum membatalkan.</div>
            )}
            <CancelOrderDialog open={orderCancelOpen} onClose={() => setOrderCancelOpen(false)} onConfirm={cancelOrderAdmin} busy={refundBusy}
              refundWarning={o.status === "paid"} />
            {o.status === "cancelled" && (
              <div className="mt-3 space-y-2" data-testid="admin-cancelled-panel">
                <CancellationSummary order={o} audience="admin" />
                <RefundStatusBadge order={o} audience="admin" />
                {refundOf(o).required && refundOf(o).status === "PENDING" && (
                  <Button variant="outline" onClick={refundProcessing} disabled={refundBusy} className="w-full rounded-xl" data-testid="admin-refund-processing-btn">Tandai Refund Diproses</Button>
                )}
                {refundOf(o).required && refundOf(o).status !== "REFUNDED" && (
                  <div className="rounded-xl border border-[#E5E5E5] p-3 space-y-2" data-testid="admin-refund-complete-form">
                    <div className="text-xs font-bold uppercase tracking-widest text-[#5C5C5C]">Catat Refund Selesai</div>
                    <input className="w-full rounded-lg border p-2 text-sm" type="number" placeholder={`Nominal (default ${refundOf(o).amount ?? 0})`}
                      value={refundForm.amount} onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} data-testid="refund-amount-input" />
                    <select className="w-full rounded-lg border p-2 text-sm" value={refundForm.method}
                      onChange={(e) => setRefundForm({ ...refundForm, method: e.target.value })} data-testid="refund-method-select">
                      <option value="transfer_bank">Transfer Bank</option><option value="qris">QRIS</option><option value="tunai">Tunai</option><option value="lainnya">Lainnya</option>
                    </select>
                    <input className="w-full rounded-lg border p-2 text-sm" placeholder="Referensi transaksi" value={refundForm.reference}
                      onChange={(e) => setRefundForm({ ...refundForm, reference: e.target.value })} data-testid="refund-reference-input" />
                    <input className="w-full rounded-lg border p-2 text-sm" placeholder="Catatan (wajib jika referensi kosong)" value={refundForm.note}
                      onChange={(e) => setRefundForm({ ...refundForm, note: e.target.value })} data-testid="refund-note-input" />
                    <Button onClick={refundComplete} disabled={refundBusy || !(refundForm.reference || refundForm.note)} className="w-full brand-btn-primary rounded-xl" data-testid="admin-refund-complete-btn">Tandai Refund Selesai</Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>

      {o.provider_used === "biteship" && <BiteshipShipmentPanel orderId={o.id} onChanged={load} refreshKey={`${o.status}|${o.payment_status}|${o.updated_at || ""}`} />}

      {(o.provider_used !== "biteship") && (jubelioActive || hasAwb) && (
        <div className="mt-4 grid lg:grid-cols-[1fr_320px] gap-4">
          <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="jubelio-order-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-display font-bold">Jubelio</h3>
              {hasAwb ? (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-emerald-100 text-emerald-700" data-testid="jubelio-status-active">Resi Aktif</span>
              ) : (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#F0E5C0] text-[#7A5440]" data-testid="jubelio-status-none">Belum dibuat</span>
              )}
            </div>

            {hasAwb ? (
              <div className="mt-3 text-sm space-y-1" data-testid="jubelio-awb-info">
                <div className="flex items-center gap-2 flex-wrap">
                  <b>AWB / Resi:</b>
                  <span className="font-mono text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5" data-testid="awb-value">{o.awb || o.tracking_number}</span>
                  <button
                    onClick={copyAwb}
                    type="button"
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#FFEDD5] text-[#E85D04] hover:bg-[#FFD7A8] transition-colors"
                    data-testid="copy-awb-btn"
                    title="Salin nomor AWB"
                  >
                    Salin
                  </button>
                </div>
                <div><b>Shipment ID:</b> <span className="font-mono text-xs">{o.jubelio_shipment_id || "-"}</span></div>
                {o.tracking_url && (
                  <div><b>Tracking URL:</b> <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-[#E85D04] underline break-all" data-testid="tracking-url">{o.tracking_url}</a></div>
                )}
                <div><b>Kurir:</b> {o.courier_name || o.jubelio_courier || "-"} {o.courier_service && <span className="text-[#5C5C5C]">· {o.courier_service}</span>}</div>
                {o.shipping_method === "STORE_DELIVERY" && (
                  <div className="mt-1 bg-teal-50 border border-teal-200 rounded-lg p-2 text-xs text-teal-900 space-y-0.5" data-testid="store-delivery-order-info">
                    <div><b>Metode:</b> {o.shipping_method_name || "Kurir Toko"} (pengiriman oleh toko — tanpa AWB eksternal)</div>
                    <div><b>Ongkir:</b> {o.shipping_cost === 0 ? "Gratis" : formatRp(o.shipping_cost)}{o.shipping_estimate ? <> · <b>Estimasi:</b> {o.shipping_estimate}</> : null}</div>
                    {o.shipping_note && <div><b>Catatan:</b> {o.shipping_note}</div>}
                  </div>
                )}
                {o.jubelio_price != null && <div><b>Harga (Jubelio):</b> {formatRp(o.jubelio_price)}</div>}
                <div><b>Status Pengiriman:</b> <span className="font-mono text-xs uppercase">{o.shipping_status || "-"}</span></div>
                {o.jubelio_cancelled && (
                  <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
                    Shipment ini sudah dibatalkan. Alasan: {o.jubelio_cancel_reason || "-"}
                  </div>
                )}
                {jubStatus && (
                  <div className="mt-3 bg-[#FAF9F6] border border-[#E5E5E5] rounded-lg p-3 text-xs" data-testid="jubelio-status-result">
                    <div><b>Status:</b> {jubStatus.status}</div>
                    {Array.isArray(jubStatus.history) && jubStatus.history.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {jubStatus.history.map((h, i) => (
                          <li key={i} className="border-l-2 border-[#E85D04] pl-2">
                            <div className="font-semibold">{h.status}</div>
                            <div className="text-[#5C5C5C]">{h.note} · {h.time && new Date(h.time).toLocaleString("id-ID")}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Button onClick={fetchJubStatus} disabled={jubBusy} variant="outline" className="rounded-xl" data-testid="jubelio-check-status-btn">Cek Status</Button>
                  {(o.awb || o.tracking_number) &&
                    (o.tracking_status || o.shipping_status) !== "delivered" &&
                    (o.tracking_status || o.shipping_status) !== "cancelled" &&
                    !o.jubelio_cancelled && (
                    <Button
                      onClick={() => { setCancelReason(""); setCancelOpen(true); }}
                      disabled={cancelBusy}
                      className="rounded-xl bg-[#DC2F02] hover:bg-[#b02701] text-white"
                      data-testid="jubelio-cancel-awb-btn"
                    >
                      Batalkan Pengiriman
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                {o.provider_used && o.provider_used !== "jubelio" && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800" data-testid="awb-not-eligible">
                    <b>Pesanan ini belum dapat dibuatkan resi Jubelio.</b> Tarif ongkir pesanan ini tidak berasal dari Jubelio. Minta pelanggan untuk melakukan pemesanan ulang saat Jubelio aktif agar resi dapat dibuat.
                  </div>
                )}
                {o.status === "cancelled" && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800" data-testid="awb-blocked-cancelled">
                    Pesanan sudah dibatalkan — resi tidak dapat dibuat.
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => { setAwbResult(null); setAwbConfirmOpen(true); }}
                    disabled={jubBusy || o.status === "cancelled" || (o.provider_used && o.provider_used !== "jubelio")}
                    className="bg-[#4A2E1F] hover:bg-[#3a2417] rounded-xl text-white disabled:opacity-50"
                    data-testid="jubelio-create-shipment-btn"
                  >
                    {jubBusy
                      ? "Memproses..."
                      : (Array.isArray(o.shipment_history) && o.shipment_history.length > 0)
                      ? "Buat Resi Ulang"
                      : "Buat Resi"}
                  </Button>
                </div>
              </div>
            )}

            {/* Riwayat Pengiriman Sebelumnya — muncul kalau order pernah punya
                shipment yang dibatalkan. AWB lama disimpan di history dan tidak
                pernah dihapus. Tampilan kompak, tidak menabrak layout existing. */}
            {Array.isArray(o.shipment_history) && o.shipment_history.length > 0 && (
              <div
                className="mt-4 border-t border-[#E5E5E5] pt-3"
                data-testid="shipment-history-section"
              >
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#7A5440] mb-2">
                  Riwayat Pengiriman Sebelumnya
                </h3>
                <ul className="space-y-2">
                  {o.shipment_history.map((h, idx) => (
                    <li
                      key={idx}
                      className="text-xs bg-[#FAF9F6] border border-[#E5E5E5] rounded-lg px-3 py-2"
                      data-testid={`shipment-history-item-${idx}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono bg-red-50 border border-red-200 text-red-700 rounded px-1.5 py-0.5" data-testid={`shipment-history-awb-${idx}`}>
                          {h.awb || "-"}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                          Dibatalkan
                        </span>
                        {h.courier_name && (
                          <span className="text-[#5C5C5C]">{h.courier_name}{h.courier_service ? ` · ${h.courier_service}` : ""}</span>
                        )}
                      </div>
                      {h.cancel_reason && (
                        <div className="text-[#5C5C5C] mt-0.5">Alasan: {h.cancel_reason}</div>
                      )}
                      {h.cancelled_at && (
                        <div className="text-[10px] text-[#8A8A8A] mt-0.5">
                          {new Date(h.cancelled_at).toLocaleString("id-ID")}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Phase 4: Tracking Pengiriman — hanya muncul kalau ada AWB */}
            {hasAwb && (
              <div className="mt-4 border-t border-[#E5E5E5] pt-3" data-testid="admin-tracking-section">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#7A5440] mb-3 flex items-center gap-2">
                  <Search size={12} /> Tracking Pengiriman
                </h3>
                <TrackingPanel
                  data={trackingDetail || {
                    awb: o.awb || o.tracking_number,
                    tracking_url: o.tracking_url,
                    courier_name: o.courier_name,
                    courier_code: o.courier_code,
                    courier_service: o.courier_service,
                    tracking_status: o.tracking_status,
                    tracking_events: o.tracking_events || [],
                    tracking_last_sync: o.tracking_last_sync,
                    tracking_sync_error: o.tracking_sync_error,
                    tracking_response_time_ms: o.tracking_response_time_ms,
                  }}
                  onRefresh={refreshTracking}
                  role="admin"
                />
              </div>
            )}

            {/* Developer-only surfaces (last-response inspector + audit
                log) were removed from tenant UI (Feb 2026). Backend
                endpoints remain accessible for internal troubleshooting. */}
          </section>
        </div>
      )}

      {/* Buat Resi confirmation dialog */}
      <Dialog open={awbConfirmOpen} onOpenChange={setAwbConfirmOpen}>
        <DialogContent data-testid="awb-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {(Array.isArray(o.shipment_history) && o.shipment_history.length > 0)
                ? "Buat Resi Ulang"
                : "Buat Resi Jubelio"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Konfirmasi pembuatan resi Jubelio untuk pesanan ini.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#1a1a1a]" data-testid="awb-confirm-warning">
              {(Array.isArray(o.shipment_history) && o.shipment_history.length > 0)
                ? "Pengiriman sebelumnya telah dibatalkan. Apakah Anda ingin membuat resi baru untuk pesanan ini?"
                : "Apakah Anda yakin ingin membuat resi untuk pesanan ini?"}
            </p>
            <p className="text-xs text-[#5C5C5C]">
              {(Array.isArray(o.shipment_history) && o.shipment_history.length > 0)
                ? "Resi sebelumnya tetap tersimpan di riwayat pengiriman."
                : "Setelah resi berhasil dibuat, informasi pengiriman akan tersimpan pada pesanan."}
            </p>
          </div>
          <DialogFooter className="mt-3 gap-2">
            <Button variant="outline" onClick={() => setAwbConfirmOpen(false)} disabled={jubBusy} className="rounded-xl" data-testid="awb-cancel-btn">
              Batal
            </Button>
            <Button
              onClick={runGenerateAwb}
              disabled={jubBusy}
              className="bg-[#4A2E1F] hover:bg-[#3a2417] rounded-xl text-white"
              data-testid="awb-confirm-btn"
            >
              {jubBusy ? "Memproses..." : ((Array.isArray(o.shipment_history) && o.shipment_history.length > 0) ? "Buat Resi Baru" : "Buat Resi")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tolak Pembayaran</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#5C5C5C] mb-2">Tulis alasan penolakan. Pesan ini akan ditampilkan ke pelanggan agar mereka bisa upload ulang.</div>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Contoh: Nominal transfer tidak sesuai dengan total pesanan"
            rows={3}
            data-testid="reject-reason-input"
          />
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={busy} className="rounded-xl">Batal</Button>
            <Button onClick={rejectPayment} disabled={busy || !rejectReason.trim()} className="bg-[#DC2F02] hover:bg-[#b02701] rounded-xl" data-testid="reject-payment-confirm">
              {busy ? "Memproses..." : "Tolak Pembayaran"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 5: Batalkan AWB modal */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-awb-dialog">
          <DialogHeader>
            <DialogTitle>Batalkan Pengiriman</DialogTitle>
            <DialogDescription className="sr-only">
              Konfirmasi pembatalan pengiriman di Jubelio.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900" data-testid="cancel-awb-warning">
              <b>Perhatian:</b> Permintaan pembatalan akan dikirim ke Jubelio dan kurir terkait ({o.courier_name || o.courier_code || "-"}). Pembatalan dapat <b>ditolak</b> oleh kurir bila paket sudah diproses. Jika ditolak, pengiriman tetap berjalan.
            </div>
            <div className="text-sm">
              <div className="text-[#7A5440] uppercase tracking-wider font-semibold text-[10px]">Nomor Resi</div>
              <div className="font-mono bg-[#FAF9F6] border border-[#E5E5E5] rounded px-2 py-1 mt-0.5 inline-block">{o.awb || o.tracking_number || "-"}</div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#7A5440]">
                Alasan Pembatalan <span className="text-red-600">*</span>
              </label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Contoh: Barang belum siap dikirim / Pesanan pelanggan dibatalkan / Stok habis"
                rows={3}
                className="mt-1"
                data-testid="cancel-reason-input"
                disabled={cancelBusy}
              />
              <div className="text-[10px] text-[#5C5C5C] mt-1">
                Minimal 3 karakter.
              </div>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button
              variant="outline"
              onClick={() => setCancelOpen(false)}
              disabled={cancelBusy}
              className="rounded-xl"
              data-testid="cancel-awb-dismiss"
            >
              Batal
            </Button>
            <Button
              onClick={cancelJubShipment}
              disabled={cancelBusy || cancelReason.trim().length < 3}
              className="bg-[#DC2F02] hover:bg-[#b02701] rounded-xl text-white"
              data-testid="cancel-awb-confirm"
            >
              {cancelBusy ? "Memproses..." : "Konfirmasi Pembatalan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
